import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PermissionCode } from '../lib/permissions';

interface User {
  userId: number;
  username: string;
  email: string;
  fullName: string;
  roleNames: string[];
  facilityIds: number[];
  departmentIds: number[];
  isActive: boolean;
  isMfaEnabled: boolean;
  avatarUrl?: string | null;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  // Server returns arbitrary strings; we keep the wire type loose.
  // The hasPermission() API below accepts only PermissionCode so
  // callers get the compile-time check even though we can't prevent
  // the server from sending unknown codes.
  permissions: string[];
  selectedFacilityId: number | null;
  selectedDepartmentId: number | null;
  isAuthenticated: boolean;
  setUser: (user: User) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setPermissions: (permissions: string[]) => void;
  selectFacility: (facilityId: number) => void;
  selectDepartment: (departmentId: number) => void;
  logout: () => void;
  // Accept plain string so callers passing a computed perm code
  // (e.g. loops over a permission list) still compile. Prefer
  // PERMS.X everywhere — the PERMS object is typed so you get
  // autocomplete + compile-time typo catches at the call site.
  hasPermission: (permission: PermissionCode | string) => boolean;
  /**
   * True when the user holds *any* of the listed perms. Handy for
   * buttons gated by an umbrella code (BILLING_CREATE) OR any of
   * its specific splits (CODING_ASSIGN / CLAIM_SUBMIT / ...).
   */
  hasAnyPermission: (permissions: readonly (PermissionCode | string)[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      permissions: [],
      selectedFacilityId: null,
      selectedDepartmentId: null,
      isAuthenticated: false,
      setUser: (user) => set({ user, isAuthenticated: true }),
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      setPermissions: (permissions) => set({ permissions }),
      selectFacility: (facilityId) => set({ selectedFacilityId: facilityId }),
      selectDepartment: (departmentId) => set({ selectedDepartmentId: departmentId }),
      logout: () => set({
        user: null,
        accessToken: null,
        refreshToken: null,
        permissions: [],
        selectedFacilityId: null,
        selectedDepartmentId: null,
        isAuthenticated: false,
      }),
      hasPermission: (permission) => get().permissions.includes(permission),
      hasAnyPermission: (permissions) =>
        permissions.some((p) => get().permissions.includes(p)),
    }),
    { name: 'vynahealth-auth' }
  )
);
